import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent } from '../../../../../../components/ui/card';
import { Input } from '../../../../../../components/ui/input';
import { Label } from '../../../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../../components/ui/select';
import { MultiSelect } from '../../../../../../components/ui/multi-select';
import { Plus, Factory, Trash2, QrCode, ChevronDown, ChevronRight, Mail, Save, Loader2 } from 'lucide-react';
import { DashboardProject } from '../../../../types/project';
import type { PipeSize, SetupState, SetupState1, PipeSize1, PipeUnit } from '../../../../types/setup';
import { getNumberOfSpoolsFromPipe } from '../../../../types/setup';
import { Button } from '../../../../../../components/ui/button';
import AddNewItemDialog from '../../../../../../components/ui/AddNewItemDialog';
import { AddManufacturingCategory, deleteSetupData } from '../../../../../../redux/Project/setUpSaga';
import { NumberInput } from '../../../../../../components/ui/NumberInput';
import { Dialog, DialogContent, DialogTitle, DialogHeader, DialogFooter } from '../../../../../../components/ui/dialog';
import { Notification as customNotification } from '../../../../../../components/ui/custom-notification';
import { fetchProjectList } from '../../../../../../redux/Project/action';
import { getAllPipeDetails, getListManufacturingCategory } from '../../../../../../redux/Project/setupData/action';
import { useDispatch } from 'react-redux';
import { Checkbox } from '../../../../../../components/ui/checkbox';
import { convertValueToInches, convertValueFromInches, convertValueToMm, convertValueFromMm, convertSetupDimensionToInches, convertSetupDimensionFromInches, roundSetupDimensionForUnit, getSetupDimensionDecimalPlaces, formatSetupDimensionForDisplay } from '../../../../../../utils/unitUtils';
import { isJobNumberDuplicateAt } from '../../../../utils/setupJobNumberValidation';
import { cn } from '../../../../../../lib/utils';
import { usePipeSizeQRCode } from './PipeSizeQRCodeModal';

// Unit options: display full names in dropdown, save short forms
const UNIT_OPTIONS: { value: string; label: string }[] = [
  { value: "in", label: "Inch" },
  { value: "cm", label: "Centimeter" },
  { value: "mm", label: "Millimeter" },
  { value: "ft", label: "Foot" },
  { value: "m", label: "Meter" },
];

const SETUP_UNIT_SHORT_LABELS: Record<string, string> = {
  in: 'in',
  mm: 'mm',
  cm: 'cm',
  ft: 'ft',
  m: 'm',
};

const getSetupUnitShortLabel = (unitValue: string | undefined, fallback: string): string =>
  SETUP_UNIT_SHORT_LABELS[unitValue ?? ''] ?? SETUP_UNIT_SHORT_LABELS[fallback] ?? fallback;

// Pipe Type / Mode / Sequence (Pipefitter / QR – values sent to API)
const PIPE_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'Seam', label: 'Seam' },
  { value: 'Seamless', label: 'Seamless' },
];
const PIPE_MODE_OPTIONS: { value: string; label: string }[] = [
  { value: 'Single Joint', label: 'Single Joint' },
  { value: 'Double Joint', label: 'Double Joint' },
  { value: 'Double Jointing', label: 'Double Jointing' },
];
const PIPE_SEQUENCE_OPTIONS: { value: string; label: string }[] = [
  { value: 'Sequence', label: 'Sequence' },
  { value: 'Individual', label: 'Individual' },
];
const PIPE_ROTATION_OPTIONS: { value: string; label: string }[] = [
  { value: 'Any', label: 'Any' },
  { value: 'ClockWise', label: 'ClockWise' },
  { value: 'Anti-ClockWise', label: 'Anti-ClockWise' },
];

/** Pipe data table highlighting — values must match PipeData (`tolerance` | `Range`) */
const PIPE_DATA_HIGHLIGHT_OPTIONS: { value: 'tolerance' | 'Range'; label: string; description: string }[] = [
  {
    value: 'tolerance',
    label: 'Tolerance (min / max)',
    description: 'Highlight values outside the minimum and maximum ID/OD entered below.',
  },
  {
    value: 'Range',
    label: 'Range (from nominal)',
    description: 'Highlight using bands measured from nominal (summary-style limits).',
  },
];

interface CommonSetupFormProps {
  project: DashboardProject;
  state: SetupState1;
  setState: React.Dispatch<React.SetStateAction<SetupState1>>;
  title: string;
  icon: React.ReactNode;
  iconColor: string;
  additionalFields?: (pipe: PipeSize1, index: number, helpers?: {
    handleUnitChange: (index: number, field: keyof PipeUnit, value: string) => void;
    UNIT_OPTIONS: { value: string; label: string }[];
    defaultUnit?: string;
    canEdit?: boolean;
    showValidation?: boolean;
    validateField?: (value: any) => boolean;
    isChangingPipeCount?: boolean;
  }) => React.ReactNode;
  manufacturingList?: any[];
  setManufacturingList: React.Dispatch<React.SetStateAction<any[]>>;
  unit: string;
  setUnit?: (unit: string) => void;
  showValidation?: boolean;
  setShowValidation?: React.Dispatch<React.SetStateAction<boolean>>;
  setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
  isShowTitle?: boolean;
  OnSave?: (hasUnsavedChanges: boolean) => void;
  /** Save a single pipe row without reloading the full setup. */
  onSavePipe?: (pipeIndex: number) => Promise<boolean>;
  canEdit?: boolean;
}

export const CommonSetupForm: React.FC<CommonSetupFormProps> = ({
  project,
  state,
  setState,
  title,
  isShowTitle = true,
  icon,
  iconColor,
  additionalFields,
  manufacturingList,
  setManufacturingList,
  unit,
  setUnit = (unit: string) => { },
  showValidation = true,
  setShowValidation,
  setHasUnsavedChanges,
  OnSave,
  onSavePipe,
  canEdit = true
}) => {
  const [isAddingManufacturer, setIsAddingManufacturer] = useState(false);
  const [pipeValidationIndices, setPipeValidationIndices] = useState<Set<number>>(new Set());
  const [savingPipeIndex, setSavingPipeIndex] = useState<number | null>(null);
  const [currentEditingPipeIndex, setCurrentEditingPipeIndex] = useState<number | null>(null);
  const [expandedPipes, setExpandedPipes] = useState<Set<number>>(new Set([0]));
  const [expandedPipefitterConfigByPipe, setExpandedPipefitterConfigByPipe] = useState<Set<number>>(new Set());
  const [expandedOptifitByPipe, setExpandedOptifitByPipe] = useState<Set<number>>(new Set());

  const togglePipeExpand = (index: number) => {
    setExpandedPipes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const togglePipefitterConfigExpand = (index: number) => {
    setExpandedPipefitterConfigByPipe((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleOptifitExpand = (index: number) => {
    setExpandedOptifitByPipe((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  // Delete confirmation dialog state
  // Delete confirmation dialog state
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [newPipeIndexes, setNewPipeIndexes] = useState<Set<number>>(new Set());
  const [numberOfPipeSizes, setNumberOfPipeSizes] = useState<number | null>(null);

  // Confirmation dialog for decrease only
  const [isChangeModalVisible, setIsChangeModalVisible] = useState(false);
  const [changeAction, setChangeAction] = useState<'decrease' | null>(null);
  const [newPipeCount, setNewPipeCount] = useState(0);
  const [originalPipeCount, setOriginalPipeCount] = useState(0);
  const [originalPipeSizes, setOriginalPipeSizes] = useState<PipeSize1[]>([]);
  const [hasShownFocusDialog, setHasShownFocusDialog] = useState(false);
  const [isChangingPipeCount, setIsChangingPipeCount] = useState(false);
  // Track last saved jobNumbers for each pipe to restore when fields are empty
  const [savedJobNumbers, setSavedJobNumbers] = useState<Record<number, string[]>>({});
  const dispatch = useDispatch();
  const [defaultUnit, setDefaultUnit] = useState('in');
  const pipeQR = usePipeSizeQRCode({
    projectNumber: project?.project?.projectNumber,
    projectName: project?.project?.projectName,
    pipeSizes: state.pipeSizes,
    projectInfo: {
      startDate: project?.project?.startDate ?? null,
      endDate: project?.project?.endDate ?? null,
      location: project?.project?.location ?? null,
      customerName: project?.project?.customer ?? null,
    },
  });

  // Show pipefitter config fields only when project has pipefitter feature and tab enabled
  const isPipefitterEnabled = useMemo(() => {
    const pt = project?.project?.projectTypes;
    if (!pt) return false;
    if (pt.pipeline?.features?.pipeFitter?.enabled) return true;
    if (pt.others?.features?.pipeFitter?.enabled) return true;
    const fab = pt.fabrication?.options;
    if (fab?.buckleArrestor?.features?.pipeFitter?.enabled) return true;
    if (fab?.pipeInPipe?.features?.pipeFitter?.enabled) return true;
    if (fab?.bulkhead?.features?.pipeFitter?.enabled) return true;
    return false;
  }, [project]);

  useEffect(() => {
    if (project.project.unit) {
      setDefaultUnit(project.project.unit || 'in');
      setUnit(project.project.unit);
    }
  }, [project.project.unit]);

  // Removed this useEffect as it was conflicting with parent component logic
  // useEffect(() => {
  //   setHasUnsavedChanges(true);
  // }, [state]);

  useEffect(() => {
    setNumberOfPipeSizes(Number(state.numberOfPipeSizes));
    // Initialize originalPipeSizes with current state data when component loads
    if (state.pipeSizes.length > 0) {
      setOriginalPipeSizes([...state.pipeSizes]);
      setOriginalPipeCount(state.pipeSizes.length);
      // Initialize saved jobNumbers from existing data
      const saved: Record<number, string[]> = {};
      state.pipeSizes.forEach((pipe, index) => {
        if (pipe.jobNumber?.jobNumbers && Array.isArray(pipe.jobNumber.jobNumbers) && pipe.jobNumber.jobNumbers.length > 0) {
          // Only save non-empty values
          const jobNumbersArray = pipe.jobNumber.jobNumbers as string[];
          const validJobNumbers = jobNumbersArray.filter((jn: string) => jn && typeof jn === 'string' && jn.trim() !== '');
          if (validJobNumbers.length > 0) {
            saved[index] = [...validJobNumbers];
          }
        }
      });
      setSavedJobNumbers(prev => {
        // Merge with existing saved values, only update if we have new valid data
        const merged = { ...prev };
        Object.keys(saved).forEach(key => {
          const idx = parseInt(key);
          if (saved[idx] && saved[idx].length > 0) {
            merged[idx] = saved[idx];
          }
        });
        return merged;
      });
      // Removed setHasUnsavedChanges call - let parent component handle this
    }
  }, [state.pipeSizes.length, state.numberOfPipeSizes]);

  // Helper function to check if a pipe has existing values
  const hasExistingValues = (pipe: PipeSize1): boolean => {

    if (pipe.isActive === false) {
      return false;
    }
    // Check if pipeName is not empty
    const hasPipeName = !!(pipe.pipeName && pipe.pipeName.toString().trim() !== '');

    // Check if manufacturer is selected (not 0)
    // const hasManufacturer = pipe.manufacturerId !== 0;

    const hasManufacturerIds = pipe.manufacturerIds && pipe.manufacturerIds.length > 0;

    // Check if prefixAssemblyName is not empty
    const hasPrefixAssemblyName = !!(pipe.prefixAssemblyName && pipe.prefixAssemblyName.trim() !== '');

    // Check if jobNumber is not empty (legacy field)
    const hasJobNumber = !!(pipe.jobNumber && pipe.jobNumber.numberOfJobs > 0);

    // Check if numberOfJobs is set and jobNumbers array has values
    const hasJobNumbers = !!(pipe.jobNumber?.numberOfJobs && pipe.jobNumber.numberOfJobs > 0 &&
      pipe.jobNumber.jobNumbers && pipe.jobNumber.jobNumbers.length > 0 &&
      pipe.jobNumber.jobNumbers.some((jobNumber: any) => jobNumber && jobNumber.trim() !== ''));

    // Check if any of the main numeric fields have non-zero values
    const hasMainNumericFields = pipe.pipeSize !== 0 || pipe.wallThickness !== 0 ||
      pipe.numberOfPipes !== 0 || pipe.pipeLength !== 0;

    // Debug: Check individual field values
    const fieldValues = {
      pipeSize: pipe.pipeSize,
      wallThickness: pipe.wallThickness,
      numberOfPipes: pipe.numberOfPipes,
      pipeLength: pipe.pipeLength
    };

    // Check fabrication-specific fields based on project type and subtype
    let hasFabricationFields = false;
    if (state.projectType === 'fabrication') {
      if (state.projectSubType === 'buckleArrestor') {
        hasFabricationFields = pipe.numberOfBuckleArrestors !== 0 ||
          pipe.numberOfForges !== 0 || pipe.outputAssemblyLength !== 0;
      } else if (state.projectSubType === 'bulkhead') {
        hasFabricationFields = pipe.numberOfBuckleArrestors !== 0 || pipe.outputAssemblyLength !== 0;
      } else if (state.projectSubType === 'pipeInPipe') {
        // For pipeInPipe, no additional fabrication fields to check
        hasFabricationFields = false;
      }
    }
    // For non-fabrication projects, fabrication fields are not relevant
    // hasFabricationFields remains false


    // Only consider it as having values if pipeName is filled OR manufacturer is selected OR any main numeric fields are filled OR prefixAssemblyName is filled OR jobNumber is filled OR jobNumbers are filled
    return hasPipeName || hasManufacturerIds || hasMainNumericFields || hasFabricationFields || hasPrefixAssemblyName || hasJobNumber || hasJobNumbers;
  };

  // Helper function to check if any pipes have existing values
  const hasAnyExistingValues = (): boolean => {
    return state.pipeSizes.some(pipe => hasExistingValues(pipe));
  };

  // Helper function to check if any pipes are active
  const hasAnyActivePipes = (): boolean => {
    return state.pipeSizes.some(pipe => pipe.isActive === true);
  };

  // Helper function to check if pipes have valid/entered values
  const hasValidPipeValues = (pipes: PipeSize1[]): boolean => {
    return pipes.some(pipe => hasExistingValues(pipe));
  };

  // Helper function to check if pipes that will be deleted have valid values
  const checkValidPipesToDelete = (currentCount: number, newCount: number): boolean => {
    // Get pipes that will be deleted (from newCount onwards)
    const pipesToDelete = state.pipeSizes.slice(newCount);
    return pipesToDelete.some(pipe => hasExistingValues(pipe));
  };

  // Reset focus dialog state when active pipes change
  useEffect(() => {
    setHasShownFocusDialog(false);
  }, [state.pipeSizes]);

  // Reset isChangingPipeCount when pipe sizes change
  useEffect(() => {
    if (isChangingPipeCount) {
      // Reset the flag after a short delay to allow the UI to update
      const timer = setTimeout(() => {
        setIsChangingPipeCount(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [state.pipeSizes, isChangingPipeCount]);

  // Validation function for individual fields
  const validateField = (value: any, fieldName: string): boolean => {
    // Check for null, undefined, or empty string
    if (value === null || value === undefined || value === '') {
      return false;
    }

    // Check for empty string after trimming
    if (typeof value === 'string' && value.trim() === '') {
      return false;
    }

    // Check for zero values (numeric fields)
    if (typeof value === 'number' && value === 0) {
      return false;
    }

    // Check for string that represents zero
    if (typeof value === 'string' && parseFloat(value) === 0) {
      return false;
    }

    // Special validation for jobNumbers array
    if (fieldName === 'jobNumber' && Array.isArray(value)) {
      return value.every(jobNumber => jobNumber && jobNumber.trim() !== '');
    }

    // Special validation for manufacturerIds array
    if (fieldName === 'manufacturerIds') {
      if (!Array.isArray(value) || value.length === 0) {
        return false;
      }
      // Check if all values in the array are valid (not 0, null, or undefined)
      return value.every(id => id !== 0 && id !== null && id !== undefined);
    }

    return true;
  };

  // Function to check if any field in a pipe has a value
  const hasAnyFieldValue = (pipe: PipeSize1): boolean => {
    // Check basic fields
    if (pipe.pipeSize && pipe.pipeSize !== 0) return true;
    if (pipe.wallThickness && pipe.wallThickness !== 0) return true;
    if (pipe.numberOfPipes && pipe.numberOfPipes !== 0) return true;
    if (pipe.pipeLength && pipe.pipeLength !== 0) return true;
    if (pipe.manufacturerId && pipe.manufacturerId !== 0) return true;
    if (pipe.manufacturerIds && Array.isArray(pipe.manufacturerIds) && pipe.manufacturerIds.length > 0) return true;
    if (pipe.prefixAssemblyName && pipe.prefixAssemblyName.trim() !== '') return true;
    if (pipe.jobNumber && pipe.jobNumber.numberOfJobs > 0) return true;

    // Check fabrication-specific fields
    if (state.projectType === 'fabrication') {
      if (pipe.numberOfBuckleArrestors !== undefined && pipe.numberOfBuckleArrestors !== 0) return true;
      if (pipe.numberOfForges !== undefined && pipe.numberOfForges !== 0) return true;
      if (pipe.outputAssemblyLength !== undefined && pipe.outputAssemblyLength !== 0) return true;
    }

    return false;
  };

  const shouldShowJobNumberFields =
    state.projectType === 'pipeline' ||
    (state.projectType === 'fabrication' && state.projectSubType !== 'spools');

  const isDuplicateJobNumber = (pipeIndex: number, jobIndex: number): boolean => {
    if (!shouldShowJobNumberFields) return false;
    return isJobNumberDuplicateAt(state.pipeSizes, pipeIndex, jobIndex);
  };

  const shouldValidatePipe = (index: number, force = false) =>
    force || showValidation || pipeValidationIndices.has(index);

  // Validation function for pipe data - validate on global save or per-pipe save
  const validatePipeData = (
    pipe: PipeSize1,
    index: number,
    force = false
  ): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    const pipeNumber = index + 1;

    if (!shouldValidatePipe(index, force)) {
      return { isValid: true, errors: [] };
    }



    if (!validateField(pipe.pipeSize, 'pipeSize')) {
      errors.push(`Pipe size is required and cannot be zero`);
    }

    if (!validateField(pipe.wallThickness, 'wallThickness')) {
      errors.push(`Wall thickness is required and cannot be zero`);
    }

    const isSpoolsFabrication =
      state.projectType === 'fabrication' && state.projectSubType === 'spools';

    if (isSpoolsFabrication) {
      const spoolCount = getNumberOfSpoolsFromPipe(pipe);
      if (spoolCount <= 0) {
        errors.push(`Number of spools is required and cannot be zero`);
      }
      // Only validate spool rows 1..spoolCount (ignore trimmed slots after count is reduced).
    } else {
      if (!validateField(pipe.numberOfPipes, 'numberOfPipes')) {
        errors.push(`Number of pipes is required and cannot be zero`);
      }

      if (!validateField(pipe.pipeLength, 'pipeLength')) {
        errors.push(`Pipe length is required and cannot be zero`);
      }
    }

    if (!validateField(pipe.manufacturerIds, 'manufacturerIds')) {
      errors.push(`Manufacturer is required`);
    }

    // if (!validateField(pipe.prefixAssemblyName, 'prefixAssemblyName')) {
    //   errors.push(`Prefix assembly name is required`);
    // }

    // // Validate job numbers - check if numberOfJobs is set and jobNumbers array is valid
    // if (!validateField(pipe.numberOfJobs, 'numberOfJobs')) {
    //   errors.push(`Number of jobs is required`);
    // } else if (pipe.numberOfJobs && pipe.numberOfJobs > 0) {
    //   if (!pipe.jobNumbers || pipe.jobNumbers.length !== pipe.numberOfJobs) {
    //     errors.push(`Job numbers array length must match number of jobs`);
    //   } else if (!validateField(pipe.jobNumbers, 'jobNumber')) {
    //     errors.push(`All job numbers are required`);
    //   }
    // }

    if (state.projectType === 'fabrication') {
      // Validate additional fields if they exist
      if (state.projectSubType == 'buckleArrestor' && pipe.numberOfBuckleArrestors !== undefined && !validateField(pipe.numberOfBuckleArrestors, 'numberOfBuckleArrestors')) {
        errors.push(`Number of buckle arrestors is required and cannot be zero`);
      }
      if (state.projectSubType == 'bulkhead' && pipe.numberOfBuckleArrestors !== undefined && !validateField(pipe.numberOfBuckleArrestors, 'numberOfBuckleArrestors')) {
        errors.push(`Number of bulk head  is required and cannot be zero`);
      }

      if (state.projectSubType == 'buckleArrestor' && pipe.numberOfForges !== undefined && !validateField(pipe.numberOfForges, 'numberOfForges')) {
        errors.push(`Number of forges is required and cannot be zero`);
      }

      // Only validate outputAssemblyLength for buckleArrestor and bulkhead subtypes
      if ((state.projectSubType == 'buckleArrestor' || state.projectSubType == 'bulkhead') && pipe.outputAssemblyLength !== undefined && !validateField(pipe.outputAssemblyLength, 'outputAssemblyLength')) {
        errors.push(`Output assembly length is required and cannot be zero`);
      }
    }

    if (shouldShowJobNumberFields) {
      const jobNumbers = pipe.jobNumber?.jobNumbers || [];
      const numberOfJobs = pipe.jobNumber?.numberOfJobs || 0;
      for (let jobIndex = 0; jobIndex < numberOfJobs; jobIndex++) {
        const jobNumberValue = jobNumbers[jobIndex];
        if (jobNumberValue && isDuplicateJobNumber(index, jobIndex)) {
          errors.push(`Job number "${String(jobNumberValue).trim()}" is already used in another pipe`);
        }
      }
    }

    return { isValid: errors.length === 0, errors };
  };

  const handleSavePipeClick = async (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canEdit || !onSavePipe) return;

    const pipe = state.pipeSizes[index];
    if (!pipe) return;

    setPipeValidationIndices((prev) => new Set(prev).add(index));
    setExpandedPipes((prev) => new Set(prev).add(index));

    const validation = validatePipeData(pipe, index, true);
    if (!validation.isValid) {
      customNotification.warning(
        'Validation failed',
        validation.errors.join('\n') || 'Please fill the required fields for this pipe.'
      );
      return;
    }

    setSavingPipeIndex(index);
    try {
      const saved = await onSavePipe(index);
      if (saved) {
        setPipeValidationIndices((prev) => {
          const next = new Set(prev);
          next.delete(index);
          return next;
        });
      }
    } finally {
      setSavingPipeIndex(null);
    }
  };

  const handleNumberOfPipeSizesChange = (value: string) => {
    if (!canEdit) return; // Prevent changes when canEdit is false

    if (!value) {
      return;
    }
    const num = parseInt(value) || 0;
    const currentCount = state.pipeSizes.length;

    // Hide validation when pipe count changes
    setShowValidation?.(false);

    // Check if pipes that will be deleted have any field values
    let hasPipesWithValues = false;
    if (num < currentCount) {
      // Check if any of the pipes that will be deleted have any field values
      for (let i = num; i < currentCount; i++) {
        if (state.pipeSizes[i] && hasAnyFieldValue(state.pipeSizes[i])) {
          hasPipesWithValues = true;
          break;
        }
      }
    }

    // Only show confirmation when decreasing and the pipes to be deleted have any field values
    if (hasPipesWithValues) {
      setNewPipeCount(num);
      setOriginalPipeCount(currentCount);
      setOriginalPipeSizes([...state.pipeSizes]); // Store the original pipe sizes
      setChangeAction('decrease');
      setIsChangeModalVisible(true);
      // Update the UI to show the new value while keeping the original state
      setNumberOfPipeSizes(num);
      setIsChangingPipeCount(false)
      return;
    }
    else {
      setIsChangeModalVisible(false);
    }
    setNumberOfPipeSizes(num);
    setIsChangingPipeCount(true)
    // Removed setHasUnsavedChanges call - let parent component handle this
    // For increase or no pipes with values to delete, proceed without confirmation
    updatePipeSizes(num);
  };



  const updatePipeSizes = (num: number) => {
    const currentCount = state.pipeSizes.length;
    const newIndexes = new Set<number>();

    // Set flag to indicate pipe count is changing
    setIsChangingPipeCount(true);

    setState(prev => {
      const newPipeSizes = Array(num).fill(null).map((_, index) => {
        // If this index exists in current pipes, preserve the existing data
        if (index < currentCount && prev.pipeSizes[index]) {
          return prev.pipeSizes[index];
        }

        // For new pipes, create with default values
        newIndexes.add(index);
        return {
          pipeId: undefined, // New pipes don't have pipeId yet
          pipeName: 'Pipe ' + (index + 1),
          pipeSize: 0,
          wallThickness: 0,
          numberOfBuckleArrestors: 0,
          numberOfPipes: 0,
          pipeLength: 0,
          numberOfForges: 0,
          outputAssemblyLength: 0,
          manufacturerId: 0,
          manufacturerIds: [],
          pipeOrder: index + 1,
          isActive: false,
          prefixAssemblyName: 'Assy',
          groupId: '',
          jobNumber: {
            numberOfJobs: 1,
            jobNumbers: ['']
          },
          units: { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' },
          documentsData: {
            numberOfDocs: 0,
            isDocumentsAvailable: false,
            documentIds: [],
            documentFileNames: []
          }
        };
      });

      return {
        ...prev,
        numberOfPipeSizes: num,
        pipeSizes: newPipeSizes
      };
    });

    // Update the new pipe indexes
    setNewPipeIndexes(newIndexes);
  };

  const handlePipeSizeChange = (index: number, field: keyof PipeSize1, value: string | number | number[] | string[] | any) => {
    if (!canEdit) return; // Prevent changes when canEdit is false

    setState(prev => {
      const pipe = prev.pipeSizes[index];
      if (!pipe) return prev;

      let nextValue: typeof value = value;
      if (field === 'pipeSize' || field === 'wallThickness') {
        const unitKey = field as 'pipeSize' | 'wallThickness';
        const fieldUnit = pipe.units?.[unitKey] ?? defaultUnit;
        if (value === '' || value == null) {
          nextValue = 0;
        } else {
          const numeric = typeof value === 'string' ? Number(value) : Number(value);
          nextValue = Number.isFinite(numeric)
            ? roundSetupDimensionForUnit(numeric, fieldUnit)
            : 0;
        }
      }

      return {
        ...prev,
        pipeSizes: prev.pipeSizes.map((p, i) =>
          i === index ? { ...p, [field]: nextValue } : p
        ),
      };
    });

    // Notify parent component that there are unsaved changes
    setHasUnsavedChanges(true);
  };

  /** 6 o'clock exclusion is degrees in range 0–90 (no negatives). */
  const handleSixOclockExclusionChange = (index: number, v: string) => {
    if (v === '' || v === '-') {
      handlePipeSizeChange(index, 'sixOclockExclusionDeg', undefined);
      return;
    }
    const numeric = Number(v);
    if (Number.isNaN(numeric)) return;
    handlePipeSizeChange(index, 'sixOclockExclusionDeg', Math.min(90, Math.max(0, numeric)));
  };

  const handleUnitChange = (index: number, field: keyof PipeUnit, newUnit: string) => {
    if (!canEdit) return;
    const valueFieldMap: Partial<Record<keyof PipeUnit, keyof PipeSize1>> = {
      pipeSize: 'pipeSize',
      wallThickness: 'wallThickness',
      length: 'pipeLength',
      outputAssemblyLength: 'outputAssemblyLength',
      pipefitterUnit: 'pipeSize', // not used via this path
      // pipefitterFieldUnits is a Record, handled separately
    };
    if (!valueFieldMap[field]) return;
    const valueField = valueFieldMap[field]!;
    setState(prev => {
      const pipe = prev.pipeSizes[index];
      if (!pipe) return prev;
      const oldUnit = (pipe.units as any)?.[field] ?? defaultUnit;
      const currentValue = Number((pipe as any)[valueField]) || 0;
      const isSetupDimension = field === 'pipeSize' || field === 'wallThickness';
      const valueInInches = isSetupDimension
        ? convertSetupDimensionToInches(currentValue, oldUnit)
        : convertValueToInches(currentValue, oldUnit);
      const convertedValue = isSetupDimension
        ? convertSetupDimensionFromInches(valueInInches, newUnit)
        : convertValueFromInches(valueInInches, newUnit);
      return {
        ...prev,
        pipeSizes: prev.pipeSizes.map((p, i) =>
          i === index
            ? {
              ...p,
              [valueField]: convertedValue,
              units: {
                ...p.units,
                pipeSize: p.units?.pipeSize ?? defaultUnit,
                wallThickness: p.units?.wallThickness ?? defaultUnit,
                length: p.units?.length ?? defaultUnit,
                outputAssemblyLength: p.units?.outputAssemblyLength ?? defaultUnit,
                [field]: newUnit,
              },
            }
            : p
        ),
      };
    });
    setHasUnsavedChanges(true);
  };

  /** When a pipefitter field unit changes, re-convert that field from old unit → new unit. */
  const handlePipefitterFieldUnitChange = (index: number, fieldKey: string, newUnit: string) => {
    if (!canEdit) return;
    // Length fields are ft-only (no unit conversion); HiLo / Nominal OD are mm-only.
    const fixedUnitFields = new Set(['nominalPipeLength', 'targetPipelineLength', 'hiloThreshold', 'nominalOd']);
    if (fixedUnitFields.has(fieldKey)) return;

    setState(prev => {
      const pipe = prev.pipeSizes[index];
      if (!pipe) return prev;
      const fieldUnits: Record<string, string> = pipe.units?.pipefitterFieldUnits ?? {};
      const oldUnit = fieldUnits[fieldKey] ?? 'mm';
      if (oldUnit === newUnit) return prev;
      const cur = (pipe as any)[fieldKey];
      const updates: Partial<PipeSize1> = {};
      if (cur != null && cur !== '' && Number(cur) !== 0) {
        const inMm = convertValueToMm(Number(cur), oldUnit);
        (updates as any)[fieldKey] = convertValueFromMm(inMm, newUnit);
      }
      return {
        ...prev,
        pipeSizes: prev.pipeSizes.map((p, i) =>
          i === index
            ? {
              ...p,
              ...updates,
              units: {
                ...p.units,
                pipefitterFieldUnits: { ...fieldUnits, [fieldKey]: newUnit },
              },
            }
            : p
        ),
      };
    });
    setHasUnsavedChanges(true);
  };



  const handleAddManufacturer = async (manufacturerName: string) => {
    if (!manufacturerName || manufacturerName.trim() === '') {
      customNotification.warning('Manufacturer name is required');
      return;
    }

    if (manufacturingList?.find(mfr => mfr.manufacturer_name === manufacturerName)) {
      customNotification.warning(`${manufacturerName} already exists`, 'Please enter a different manufacturer name');
      return;
    }

    const newManufacturer = {
      manufacturer_name: manufacturerName,
      manufacturer_type: 'Pipe'
    };

    const res = await AddManufacturingCategory(newManufacturer);
    if (res) {
      // Force refresh manufacturing categories from API after adding
      dispatch(getListManufacturingCategory({}));

      // Only update the manufacturing list, don't set state
      setManufacturingList((prev: any[]) => [...prev, res]);
      if (currentEditingPipeIndex !== null) {
        state.pipeSizes[currentEditingPipeIndex].manufacturerId = res.manufacturer_id;
        state.pipeSizes[currentEditingPipeIndex].manufacturerIds = [...(state.pipeSizes[currentEditingPipeIndex].manufacturerIds || []), res.manufacturer_id];
      }
    }
  };

  const handleDeletePipeSize = (index: number) => {
    const pipe = state.pipeSizes[index];

    // Hide validation when deleting pipes
    setShowValidation?.(false);

    // If pipe has any field values, show confirmation dialog
    if (hasAnyFieldValue(pipe)) {
      setItemToDelete({ ...pipe, index });
      setIsDeleteModalVisible(true);
    } else {
      // If pipe has no field values, delete immediately without confirmation
      setState(prev => ({
        ...prev,
        numberOfPipeSizes: prev.numberOfPipeSizes - 1,
        pipeSizes: prev.pipeSizes.filter((_, i) => i !== index)
      }));
      setNumberOfPipeSizes(Number(state.numberOfPipeSizes) - 1);
    }
  }

  // Add delete confirmation handlers
  const handleCancelDelete = () => {
    setIsDeleteModalVisible(false);
    setItemToDelete(null);
    setDeleteLoading(false);
    // No need to restore state for delete cancel since we haven't changed anything yet

    // Hide validation when canceling pipe deletion
    setShowValidation?.(false);
  };

  const handleConfirmDelete = async () => {
    try {
      setDeleteLoading(true);

      // Here you would typically make an API call to delete the item
      // For now, we'll simulate an API call with a timeout



      if (hasExistingValues(itemToDelete)) {
        const payload = {
          "data": {
            "pipeIds": [itemToDelete?.pipeId]
          }
        }
        const res = await deleteSetupData(payload);
        if (res.statusCode === 200) {
          // Refresh pipe details from API after successful delete
          dispatch(getAllPipeDetails({ projectId: project.project.projectId }));

          // Show success message
          setState(prev => ({
            ...prev,
            numberOfPipeSizes: prev.numberOfPipeSizes - 1,
            pipeSizes: prev.pipeSizes.filter((_, i) => i !== itemToDelete?.index)
          }));
          setNumberOfPipeSizes(state.numberOfPipeSizes - 1);

          customNotification.success(`successfully deleted  Pipe ${itemToDelete?.index + 1} details`, `The ${title} has been deleted successfully.`);
        }
      }
      else {
        setState(prev => ({
          ...prev,
          numberOfPipeSizes: prev.numberOfPipeSizes - 1,
          pipeSizes: prev.pipeSizes.filter((_, i) => i !== itemToDelete?.index)
        }));
        setNumberOfPipeSizes(state.numberOfPipeSizes - 1);

        customNotification.success(`successfully deleted ${itemToDelete?.pipeName ? 'pipe details' : 'pipe'}`, `The ${title} has been deleted successfully.`);
      }
      setIsDeleteModalVisible(false);
      setItemToDelete(null);
      setHasUnsavedChanges(false);
      // Hide validation after confirming pipe deletion
      setShowValidation?.(false);

      dispatch(fetchProjectList()); // Fetch project list to update the project list in the dashboard 
    } catch (error) {
      customNotification.error('Failed to delete item. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle decrease confirmation
  const handleCancelChange = () => {


    // Only restore if we have valid original data
    if (originalPipeCount > 0 && originalPipeSizes.length > 0) {
      setState(prev => ({
        ...prev,
        numberOfPipeSizes: originalPipeCount,
        pipeSizes: originalPipeSizes
      }));
      setNumberOfPipeSizes(originalPipeCount);
    } else {
      // Fallback: restore to current state
      setNumberOfPipeSizes(state.numberOfPipeSizes);
    }

    setIsChangeModalVisible(false);
    setChangeAction(null);
    setNewPipeCount(0);
    setOriginalPipeCount(0);
    setOriginalPipeSizes([]);

    // Hide validation when canceling pipe count change
    setShowValidation?.(false);
  };

  const handleConfirmChange = async () => {
    // Apply the decreased pipe count when user confirms
    setDeleteLoading(true);
    try {
      // Get pipes that will be deleted (from newPipeCount onwards)
      const pipesToDelete = originalPipeSizes.slice(newPipeCount);
      const validPipesToDelete = pipesToDelete.filter(pipe => hasExistingValues(pipe));

      // If there are pipes with valid values to delete, make API call
      if (validPipesToDelete.length > 0) {
        const pipeIds = validPipesToDelete.map(pipe => pipe.pipeId).filter(id => id !== undefined);

        if (pipeIds.length > 0) {
          const payload = {
            "data": {
              "pipeIds": pipeIds
            }
          };

          const res = await deleteSetupData(payload);

          if (res.statusCode === 200) {
            // Refresh pipe details from API after successful delete
            dispatch(getAllPipeDetails({ projectId: project.project.projectId }));

            customNotification.success(`Successfully deleted ${validPipesToDelete.length} pipe details`, `The ${title} has been deleted successfully.`);
          }
        }
      }
      else {
        customNotification.success(`Successfully deleted ${pipesToDelete.length} pipe details`, `The ${title} has been deleted successfully.`);
      }
      // Update the state to reflect the decreased pipe count
      updatePipeSizes(newPipeCount);
      setNumberOfPipeSizes(newPipeCount);
      setIsChangeModalVisible(false);
      setChangeAction(null);
      setNewPipeCount(0);
      setOriginalPipeCount(0);
      setOriginalPipeSizes([]);
      setHasUnsavedChanges(false);
      // Hide validation after confirming pipe count change
      setShowValidation?.(false)
      setDeleteLoading(false);

      dispatch(fetchProjectList()); // Fetch project list to update the project list in the dashboard 
    } catch (error) {
      setDeleteLoading(false);
      customNotification.error('Failed to delete pipe details. Please try again.');
    }
    setDeleteLoading(false);
  };

  return (
    <div className="space-y-8 mb-12">
      {/* {isShowTitle && <div className="flex items-center gap-2">
        <span className="text-heritage-grey">{icon}</span>
        <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">{title}</h3>
      </div>
      } */}
      {/* Number of Pipe Sizes Input */}
      <div className="space-y-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-full max-w-xs">
            <Label htmlFor="numberOfPipeSizes" className="text-[12px] font-medium text-text-grey">
              Number of Pipe Sizes
            </Label>
            <NumberInput
              id="numberOfPipeSizes"
              min={0}
              step={1}
              max={100}
              value={numberOfPipeSizes === null ? state.numberOfPipeSizes : numberOfPipeSizes}
              onChange={(value) => handleNumberOfPipeSizesChange(String(value))}
              className="mt-2"
              placeholder="Enter number of pipe sizes"
              disabled={!canEdit}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                // const value = e.target.value;
                //  if(!value && numberOfPipeSizes !== null){
                //   setNumberOfPipeSizes(state.numberOfPipeSizes);
                //   setState(prev => ({
                //     ...prev,
                //     numberOfPipeSizes: state.numberOfPipeSizes
                //   }));
                //  }

              }}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-8 h-9 px-3"
            onClick={() => void pipeQR.showAll()}
            disabled={pipeQR.generating || !state.pipeSizes.length}
            title="Generate QR code with project and pipe details"
          >
            <QrCode className="h-4 w-4 mr-2" />
            {pipeQR.generating ? 'Generating...' : 'Generate QR Code'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-8 h-9 px-3"
            onClick={() => pipeQR.openAllPipesEmailModal()}
            disabled={
              pipeQR.allPipesEmailSending ||
              pipeQR.generating ||
              !state.pipeSizes.length
            }
            title="Email all pipe QR codes as a single PDF"
          >
            <Mail className="h-4 w-4 mr-2" />
            {pipeQR.allPipesEmailSending ? 'Sending...' : 'Email All Pipe QR'}
          </Button>
        </div>
      </div>

      {/* Pipe Sizes Inputs */}
      {state.pipeSizes.length > 0 && (
        <div className="space-y-6">
          <h4 className="font-heading text-[14px] font-semibold text-blueprint-blue">Pipe Size Details</h4>
          {state.pipeSizes.map((pipe, index) => {
            const pipeShowValidation = showValidation || pipeValidationIndices.has(index);
            const validation = validatePipeData(pipe, index);
            const hasErrors = pipeShowValidation && !isChangingPipeCount && !validation.isValid;
            const isExpanded = expandedPipes.has(index) || hasErrors;
            const isReducer = !!(pipe.pipeDetailsExtraDetails && typeof pipe.pipeDetailsExtraDetails === 'object' && 'isReducerPipe' in pipe.pipeDetailsExtraDetails && pipe.pipeDetailsExtraDetails.isReducerPipe);
            const isFlange = !!(pipe.pipeDetailsExtraDetails && typeof pipe.pipeDetailsExtraDetails === 'object' && 'isFlangePipe' in pipe.pipeDetailsExtraDetails && pipe.pipeDetailsExtraDetails.isFlangePipe);
            const groupId =
              pipe.groupId != null && String(pipe.groupId).trim() !== ''
                ? String(pipe.groupId).trim()
                : '';
            const pipeSizeUnit = pipe.units?.pipeSize || unit || defaultUnit;
            const wallThicknessUnit = pipe.units?.wallThickness || unit || defaultUnit;
            return (
              <Card key={index} className={`border overflow-visible ${hasErrors ? 'bg-red-50 border-red-200' : 'border-gray-200'}`}>
                {/* Summary row — always visible, toggles expansion */}
                <div className="flex items-center gap-3 px-4 py-3">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => togglePipeExpand(index)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePipeExpand(index); } }}
                    className="flex-1 flex items-center gap-3 cursor-pointer min-w-0 hover:opacity-80 transition-opacity"
                  >
                    {isExpanded ? <ChevronDown className="h-4 w-4 text-heritage-grey flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-heritage-grey flex-shrink-0" />}
                    <span className="font-heading text-[13px] font-semibold text-blueprint-blue flex-shrink-0">Pipe {index + 1}</span>
                    <span className="text-[12px] text-text-grey truncate">
                      {pipe.pipeSize ? formatSetupDimensionForDisplay(pipe.pipeSize, pipeSizeUnit) : '—'}{getSetupUnitShortLabel(pipeSizeUnit, defaultUnit)} × {pipe.wallThickness ? formatSetupDimensionForDisplay(pipe.wallThickness, wallThicknessUnit) : '—'}{getSetupUnitShortLabel(wallThicknessUnit, defaultUnit)}
                      {groupId ? <> · {groupId}</> : null}
                      {' · '}{pipe.numberOfPipes || 0} pipes · {pipe.pipeLength || 0}ft
                    </span>
                    {isReducer && (
                      <span className="text-[10px] font-semibold text-blueprint-blue bg-[rgba(0,39,63,0.06)] px-1.5 py-0.5 rounded flex-shrink-0">
                        Reducer
                      </span>
                    )}
                    {isFlange && (
                      <span className="text-[10px] font-semibold text-blueprint-blue bg-[rgba(0,39,63,0.06)] px-1.5 py-0.5 rounded flex-shrink-0">
                        Flange
                      </span>
                    )}
                    {hasErrors && (
                      <span className="text-[10px] font-semibold text-readable-red bg-[rgba(196,0,31,0.06)] px-1.5 py-0.5 rounded flex-shrink-0">
                        Errors
                      </span>
                    )}
                  </div>
                  {canEdit && (
                    <div className="flex-shrink-0 flex items-center gap-3">
                      {state.projectType === 'fabrication' && (
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-1.5">
                            <Checkbox
                              id={`reducer_pipe_${index}`}
                              checked={isReducer}
                              onCheckedChange={(checked) => {
                                const extra = (pipe.pipeDetailsExtraDetails && typeof pipe.pipeDetailsExtraDetails === 'object')
                                  ? { ...pipe.pipeDetailsExtraDetails as Record<string, unknown>, isReducerPipe: checked === true }
                                  : { isReducerPipe: checked === true };
                                handlePipeSizeChange(index, 'pipeDetailsExtraDetails', extra);
                              }}
                              className="flex-shrink-0"
                            />
                            <label htmlFor={`reducer_pipe_${index}`} className="text-[12px] text-text-grey cursor-pointer">
                              Reducer
                            </label>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Checkbox
                              id={`flange_pipe_${index}`}
                              checked={isFlange}
                              onCheckedChange={(checked) => {
                                const extra = (pipe.pipeDetailsExtraDetails && typeof pipe.pipeDetailsExtraDetails === 'object')
                                  ? { ...pipe.pipeDetailsExtraDetails as Record<string, unknown>, isFlangePipe: checked === true }
                                  : { isFlangePipe: checked === true };
                                handlePipeSizeChange(index, 'pipeDetailsExtraDetails', extra);
                              }}
                              className="flex-shrink-0"
                            />
                            <label htmlFor={`flange_pipe_${index}`} className="text-[12px] text-text-grey cursor-pointer">
                              Flange
                            </label>
                          </div>
                        </div>
                      )}


                      {onSavePipe && (
                        <Button
                          type="button"
                          onClick={(e) => void handleSavePipeClick(index, e)}
                          disabled={!canEdit || savingPipeIndex === index}
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Save this pipe"
                        >
                          {savingPipeIndex === index ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Save className="h-4 w-4" />
                          )}
                        </Button>
                      )}

                      <Button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          void pipeQR.showPipe(index);
                        }}
                        disabled={pipeQR.generating || state.pipeSizes.length === 0}
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0 text-blueprint-blue hover:text-blueprint-blue hover:bg-page-bg"
                        title="Show pipe QR code"
                      >
                        <QrCode className="h-4 w-4" />
                      </Button>

                      <Button
                        onClick={() => handleDeletePipeSize(index)}
                        disabled={state.pipeSizes.length === 0}
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Delete Pipe"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Expanded form content */}
                {isExpanded && (
                  <CardContent className="px-4 pt-0 pb-4 border-t border-gray-200">
                    <div className="space-y-4 pt-4">

                      {/* Validation errors display - only show when showValidation is true and not changing pipe count */}
                      {pipeShowValidation && !isChangingPipeCount && !validation.isValid && (
                        <div className="p-3 bg-red-100 border rounded-md">
                          <p className="text-sm font-medium text-red-800 mb-2">Validation Errors:</p>
                          <ul className="text-sm text-red-700 space-y-1">
                            {validation.errors.map((error, errorIndex) => (
                              <li key={errorIndex} className="flex items-start">
                                <span className="mr-2">•</span>
                                {error}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <Label className="text-[12px] font-medium text-text-grey" htmlFor={`pipeSize-${index}`}>Pipe Size <span className="text-red-500">*</span></Label>
                          <NumberInput
                            id={`pipeSize-${index}`}
                            min={0}
                            step={0.01}
                            value={pipe.pipeSize}
                            onChange={(value) => {
                              handlePipeSizeChange(index, 'pipeSize', value);
                            }}
                            showUnitSelect
                            unitValue={pipe.units?.pipeSize || unit || defaultUnit}
                            unitOptions={UNIT_OPTIONS}
                            onUnitChange={(v) => handleUnitChange(index, 'pipeSize', v)}
                            unitSelectWrapperClassName="mt-2"
                            maxDecimalPlaces={getSetupDimensionDecimalPlaces(pipe.units?.pipeSize || unit || defaultUnit)}
                            className={pipeShowValidation && !isChangingPipeCount && !validateField(pipe.pipeSize, 'pipeSize') ? 'border-red-500' : ''}
                            placeholder="Enter pipe Size"
                            disabled={!canEdit}
                          />
                          {pipeShowValidation && !isChangingPipeCount && !validateField(pipe.pipeSize, 'pipeSize') && (
                            <p className="mt-1 text-xs text-red-500">Pipe size is required and cannot be zero</p>
                          )}
                        </div>
                        <div>
                          <Label className="text-[12px] font-medium text-text-grey" htmlFor={`wallThickness-${index}`}>Wall Thickness <span className="text-red-500">*</span></Label>
                          <NumberInput
                            id={`wallThickness-${index}`}
                            min={0}
                            step={0.01}
                            value={pipe.wallThickness}
                            onChange={(value) => handlePipeSizeChange(index, 'wallThickness', value)}
                            showUnitSelect
                            unitValue={pipe.units?.wallThickness || unit || defaultUnit}
                            unitOptions={UNIT_OPTIONS}
                            onUnitChange={(v) => handleUnitChange(index, 'wallThickness', v)}
                            unitSelectWrapperClassName="mt-2"
                            maxDecimalPlaces={getSetupDimensionDecimalPlaces(pipe.units?.wallThickness || unit || defaultUnit)}
                            className={pipeShowValidation && !isChangingPipeCount && !validateField(pipe.wallThickness, 'wallThickness') ? 'border-red-500' : ''}
                            placeholder="Enter wall thickness"
                            disabled={!canEdit}
                          />
                          {pipeShowValidation && !isChangingPipeCount && !validateField(pipe.wallThickness, 'wallThickness') && (
                            <p className="mt-1 text-xs text-red-500">Wall thickness is required and cannot be zero</p>
                          )}
                        </div>
                        {state.projectSubType !== 'spools' && <div>
                          <Label className="text-[12px] font-medium text-text-grey" htmlFor={`numberOfPipes-${index}`}>Number of Pipes <span className="text-red-500">*</span></Label>
                          <NumberInput
                            id={`numberOfPipes-${index}`}
                            min={0}
                            step={1}
                            value={pipe.numberOfPipes}
                            onChange={(value) => handlePipeSizeChange(index, 'numberOfPipes', value)}
                            className={`mt-2 ${pipeShowValidation && !isChangingPipeCount && !validateField(pipe.numberOfPipes, 'numberOfPipes') ? '' : ''}`}
                            placeholder="Enter number of pipes"
                            disabled={!canEdit}
                          />
                          {pipeShowValidation && !isChangingPipeCount && !validateField(pipe.numberOfPipes, 'numberOfPipes') && (
                            <p className="mt-1 text-xs text-red-500">Number of pipes is required and cannot be zero</p>
                          )}
                        </div>}
                        {state.projectSubType !== 'spools' && <div>
                          <Label className="text-[12px] font-medium text-text-grey" htmlFor={`pipeLength-${index}`}>Length of Pipes <span className="text-red-500">*</span></Label>
                          <NumberInput
                            id={`pipeLength-${index}`}
                            min={0}
                            step={0.01}
                            value={pipe.pipeLength}
                            onChange={(value) => handlePipeSizeChange(index, 'pipeLength', value)}
                            showUnitSelect
                            unitValue={pipe.units?.length || defaultUnit}
                            unitOptions={UNIT_OPTIONS}
                            onUnitChange={(v) => handleUnitChange(index, 'length', v)}
                            unitSelectWrapperClassName="mt-2"
                            className={pipeShowValidation && !isChangingPipeCount && !validateField(pipe.pipeLength, 'pipeLength') ? 'border-red-500' : ''}
                            placeholder="Enter pipe length"
                            disabled={!canEdit}
                          />
                          {pipeShowValidation && !isChangingPipeCount && !validateField(pipe.pipeLength, 'pipeLength') && (
                            <p className="mt-1 text-xs text-red-500">Pipe length is required and cannot be zero</p>
                          )}
                        </div>}

                        {/* Additional fields specific to each setup type */}
                        {state.projectSubType !== 'spools' && additionalFields && additionalFields(pipe, index, {
                          handleUnitChange,
                          UNIT_OPTIONS,
                          defaultUnit,
                          canEdit,
                          showValidation: pipeShowValidation,
                          validateField: (v: any) => validateField(v, 'outputAssemblyLength'),
                          isChangingPipeCount,
                        })}

                        <div>
                          <Label className="text-[12px] font-medium text-text-grey" htmlFor={`manufacturer-${index}`}>Manufacturer <span className="text-red-500">*</span></Label>
                          <MultiSelect
                            options={manufacturingList && manufacturingList?.length > 0 ? manufacturingList?.map(mfr => ({
                              value: mfr.manufacturer_id.toString(),
                              label: mfr.manufacturer_name
                            })) || [] : []}
                            selected={pipe.manufacturerIds && pipe.manufacturerIds?.length > 0 ? pipe.manufacturerIds.map(id => id.toString()) : []}
                            // onChange={(selected: string[]) => {
                            //   if (selected.length > 0) {
                            //     // For now, we'll take the first selected manufacturer to maintain compatibility
                            //     const selectedId = parseInt(selected[selected.length - 1]);
                            //     handlePipeSizeChange(index, 'manufacturerIds', selectedId);
                            //   } else {
                            //     handlePipeSizeChange(index, 'manufacturerIds', 0);
                            //   }
                            // }}
                            onChange={(selected: string[]) => {
                              if (!canEdit) return; // Prevent changes when canEdit is false
                              if (selected.length > 0) {
                                handlePipeSizeChange(index, 'manufacturerIds', selected.map(id => Number(id)));
                                handlePipeSizeChange(index, 'manufacturerId', Number(selected[0]));
                              } else {
                                // Handle deselection - clear both manufacturerIds and manufacturerId
                                handlePipeSizeChange(index, 'manufacturerIds', []);
                                handlePipeSizeChange(index, 'manufacturerId', 0);
                              }
                            }
                            }
                            placeholder="Select manufacturer"
                            isSearchable={canEdit}
                            showAddButton={canEdit}
                            addButtonText="Add New Manufacturer"
                            onAddButtonClick={() => {
                              if (!canEdit) return; // Prevent opening dialog when canEdit is false
                              setCurrentEditingPipeIndex(index);
                              setIsAddingManufacturer(true);
                            }}
                            maxHeight="260px"
                            className={`mt-2 ${pipeShowValidation && !isChangingPipeCount && !validateField(pipe.manufacturerIds, 'manufacturerIds') ? '' : ''} ${!canEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
                          />
                          {pipeShowValidation && !isChangingPipeCount && !validateField(pipe.manufacturerIds, 'manufacturerIds') && (
                            <p className="mt-1 text-xs text-red-500">Manufacturer is required</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label
                            className="text-[12px] font-medium text-text-grey"
                            htmlFor={`group-id-${index}`}
                          >
                            Group ID
                          </Label>
                          <Input
                            id={`group-id-${index}`}
                            value={pipe.groupId ?? ''}
                            onChange={(e) => {
                              if (!canEdit) return;
                              handlePipeSizeChange(index, 'groupId', e.target.value);
                            }}
                            placeholder="Enter group ID"
                            disabled={!canEdit}
                            className="mt-2 bg-white border-[#E2E5EA]"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label
                            className="text-[12px] font-medium text-text-grey"
                            htmlFor={`pipe-description-${index}`}
                          >
                            Description
                          </Label>
                          <Input
                            id={`pipe-description-${index}`}
                            value={
                              pipe.pipeDetailsExtraDetails &&
                                typeof pipe.pipeDetailsExtraDetails === 'object' &&
                                typeof (pipe.pipeDetailsExtraDetails as Record<string, unknown>).pipeDescription === 'string'
                                ? String((pipe.pipeDetailsExtraDetails as Record<string, unknown>).pipeDescription)
                                : ''
                            }
                            onChange={(e) => {
                              if (!canEdit) return;
                              const extra =
                                pipe.pipeDetailsExtraDetails && typeof pipe.pipeDetailsExtraDetails === 'object'
                                  ? {
                                    ...(pipe.pipeDetailsExtraDetails as Record<string, unknown>),
                                    pipeDescription: e.target.value,
                                  }
                                  : { pipeDescription: e.target.value };
                              handlePipeSizeChange(index, 'pipeDetailsExtraDetails', extra);
                            }}
                            placeholder="Enter description"
                            disabled={!canEdit}
                            className={!canEdit ? 'opacity-60 cursor-not-allowed' : ''}
                          />
                        </div>
                        {shouldShowJobNumberFields && (
                          <div>
                            <Label className="text-[12px] font-medium text-text-grey" htmlFor={`numberOfJobs-${index}`}>Number of Job Numbers <span className="text-red-500">*</span></Label>
                            <NumberInput
                              id={`numberOfJobs-${index}`}
                              min={1}
                              step={1}
                              value={pipe.jobNumber?.numberOfJobs || 1}
                              onChange={(value) => {
                                const numJobs = Number(value);
                                const currentJobNumbers = pipe.jobNumber?.jobNumbers || [];
                                const savedValues = savedJobNumbers[index] || [];

                                // Preserve existing values when changing numberOfJobs
                                const newJobNumbers: string[] = [];
                                for (let i = 0; i < numJobs; i++) {
                                  // First try to get from current values, then from saved values, then empty string
                                  if (i < currentJobNumbers.length && currentJobNumbers[i] && currentJobNumbers[i].trim() !== '') {
                                    newJobNumbers[i] = currentJobNumbers[i];
                                  } else if (i < savedValues.length && savedValues[i] && savedValues[i].trim() !== '') {
                                    newJobNumbers[i] = savedValues[i];
                                  } else {
                                    newJobNumbers[i] = '';
                                  }
                                }

                                handlePipeSizeChange(index, 'jobNumber', {
                                  numberOfJobs: numJobs,
                                  jobNumbers: newJobNumbers
                                });
                              }}
                              className={`mt-2 ${pipeShowValidation && !isChangingPipeCount && !validateField(pipe.jobNumber?.numberOfJobs, 'numberOfJobs') ? 'border-red-500' : ''}`}
                              placeholder="Enter Job Number"
                              disabled={!canEdit}
                            />
                            {pipeShowValidation && !isChangingPipeCount && !validateField(pipe.jobNumber?.numberOfJobs, 'numberOfJobs') && (
                              <p className="mt-1 text-xs text-red-500">Job Number is required</p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Dynamic Job Numbers Section */}
                      {(shouldShowJobNumberFields && (pipe?.jobNumber?.numberOfJobs ?? 0) > 0) && (
                        <div className="mt-4">
                          <Label className="text-[12px] font-medium text-text-grey mb-3 block">Job Numbers <span className="text-red-500">*</span></Label>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {Array.from({ length: pipe.jobNumber?.numberOfJobs || 1 }, (_, jobIndex) => (
                              <div key={jobIndex}>
                                <Label htmlFor={`jobNumber-${index}-${jobIndex}`} className="text-[11px] text-heritage-grey">
                                  Job Number {jobIndex + 1}
                                </Label>
                                <Input
                                  id={`jobNumber-${index}-${jobIndex}`}
                                  value={pipe.jobNumber?.jobNumbers?.[jobIndex] || ''}
                                  onChange={(e) => {
                                    const newJobNumbers = [...(pipe.jobNumber?.jobNumbers || [])];
                                    newJobNumbers[jobIndex] = e.target.value;
                                    handlePipeSizeChange(index, 'jobNumber', {
                                      numberOfJobs: pipe.jobNumber?.numberOfJobs || 1,
                                      jobNumbers: newJobNumbers
                                    });
                                  }}
                                  onBlur={(e) => {
                                    // If field is empty, restore from saved values
                                    if (!e.target.value || e.target.value.trim() === '') {
                                      const savedValues = savedJobNumbers[index] || [];
                                      if (savedValues[jobIndex] && savedValues[jobIndex].trim() !== '') {
                                        const newJobNumbers = [...(pipe.jobNumber?.jobNumbers || [])];
                                        newJobNumbers[jobIndex] = savedValues[jobIndex];
                                        handlePipeSizeChange(index, 'jobNumber', {
                                          numberOfJobs: pipe.jobNumber?.numberOfJobs || 1,
                                          jobNumbers: newJobNumbers
                                        });
                                      }
                                    } else {
                                      // Save the value if it's not empty
                                      const currentJobNumbers = pipe.jobNumber?.jobNumbers || [];
                                      const saved = savedJobNumbers[index] || [];
                                      const updatedSaved = [...saved];
                                      // Ensure array is long enough
                                      while (updatedSaved.length <= jobIndex) {
                                        updatedSaved.push('');
                                      }
                                      updatedSaved[jobIndex] = e.target.value.trim();
                                      setSavedJobNumbers(prev => ({
                                        ...prev,
                                        [index]: updatedSaved
                                      }));
                                    }
                                  }}
                                  className={`mt-1 ${pipeShowValidation && !isChangingPipeCount && (!validateField(pipe.jobNumber?.jobNumbers?.[jobIndex], 'jobNumber') || isDuplicateJobNumber(index, jobIndex)) ? 'border-red-500' : ''}`}
                                  placeholder={`Enter Job Number ${jobIndex + 1}`}
                                  disabled={!canEdit}
                                />

                                {pipeShowValidation && !isChangingPipeCount && !validateField(pipe.jobNumber?.jobNumbers?.[jobIndex], 'jobNumber') && (
                                  <p className="mt-1 text-xs text-red-500">Job Number {jobIndex + 1} is required</p>
                                )}
                                {pipeShowValidation && !isChangingPipeCount && validateField(pipe.jobNumber?.jobNumbers?.[jobIndex], 'jobNumber') && isDuplicateJobNumber(index, jobIndex) && (
                                  <p className="mt-1 text-xs text-red-500">This job number is already used in another pipe</p>
                                )}
                              </div>

                            ))}
                          </div>
                        </div>
                      )}

                      {/* Pipefitter config – only when project type has pipefitter tab enabled */}
                      {isPipefitterEnabled && (
                        <>
                          <div className="border-t border-gray-200 pt-4 mt-4" />
                          <div className="pt-4">

                            {(() => {
                              const fieldUnits: Record<string, string> = pipe.units?.pipefitterFieldUnits ?? {};
                              // Shared mm pipefitter input (unit select); used by pfField and nominal min/max matrix
                              const renderPfMmInput = (
                                fieldKey: keyof PipeSize1,
                                placeholder: string,
                                defaultZero: boolean
                              ) => {
                                const raw = (pipe as any)[fieldKey];
                                const fieldUnit = fieldUnits[fieldKey as string] ?? 'mm';
                                const valueForInput = defaultZero
                                  ? raw === undefined || raw === null || raw === ''
                                    ? 0
                                    : Number(raw)
                                  : (raw ?? '');
                                return (
                                  <div>
                                    <NumberInput
                                      id={`${fieldKey}-${index}`}
                                      min={0}
                                      step={0.01}
                                      value={valueForInput}
                                      onChange={(v) => {
                                        if (defaultZero) {
                                          const n = v === '' ? 0 : Number(v);
                                          handlePipeSizeChange(index, fieldKey, Number.isNaN(n) ? 0 : n);
                                        } else {
                                          handlePipeSizeChange(index, fieldKey, v === '' ? undefined : Number(v));
                                        }
                                      }}
                                      showUnitSelect
                                      unitValue={fieldUnit}
                                      unitOptions={UNIT_OPTIONS}
                                      onUnitChange={(v) => handlePipefitterFieldUnitChange(index, fieldKey as string, v)}
                                      unitSelectWrapperClassName="mt-2"
                                      placeholder={placeholder}
                                      disabled={!canEdit}
                                    />
                                  </div>
                                );
                              };
                              // pfField: each field has its own independent unit (NumberInput showUnitSelect, same as pipe length)
                              const pfField = (
                                fieldKey: keyof PipeSize1,
                                label: string,
                                placeholder = '',
                                opts?: { defaultZero?: boolean }
                              ) => {
                                const defaultZero = opts?.defaultZero === true;
                                return (
                                  <div>
                                    <Label htmlFor={`${fieldKey}-${index}`}>{label}</Label>
                                    {renderPfMmInput(fieldKey, placeholder, defaultZero)}
                                  </div>
                                );
                              };
                              const pfSectionTitle = (title: string) => (
                                <p className="text-sm font-medium text-gray-700 col-span-full border-b border-gray-200 pb-2 mb-1">{title}</p>
                              );
                              const highlightMode: 'tolerance' | 'Range' =
                                pipe.highlightType === 'Range' ? 'Range' : 'tolerance';
                              const highlightDesc =
                                PIPE_DATA_HIGHLIGHT_OPTIONS.find((o) => o.value === highlightMode)?.description ?? '';
                              const isPipefitterConfigExpanded = expandedPipefitterConfigByPipe.has(index);
                              const isOptifitExpanded = expandedOptifitByPipe.has(index);

                              return (
                                <div className="space-y-4">
                                  <div className="rounded-lg border border-gray-200 bg-gray-50/70 overflow-hidden">
                                    <button
                                      type="button"
                                      onClick={() => togglePipefitterConfigExpand(index)}
                                      className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-100/80 transition-colors"
                                      aria-expanded={isPipefitterConfigExpanded}
                                    >
                                      {isPipefitterConfigExpanded ? (
                                        <ChevronDown className="h-4 w-4 text-heritage-grey flex-shrink-0" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4 text-heritage-grey flex-shrink-0" />
                                      )}
                                      <span className="text-sm font-medium text-gray-600">Pipefitter</span>
                                    </button>
                                    {isPipefitterConfigExpanded && (
                                      <div className="px-4 pb-4 pt-3 space-y-6 border-t border-gray-200">
                                        <div className="max-w-xl">
                                          <Label htmlFor={`highlightType-${index}`}>Pipe data highlighting</Label>
                                          <Select
                                            value={highlightMode}
                                            onValueChange={(v) =>
                                              handlePipeSizeChange(
                                                index,
                                                'highlightType',
                                                v === 'Range' ? 'Range' : 'tolerance'
                                              )
                                            }
                                            disabled={!canEdit}
                                          >
                                            <SelectTrigger id={`highlightType-${index}`} className="mt-2 w-full md:w-72">
                                              <SelectValue placeholder="Select highlight mode" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {PIPE_DATA_HIGHLIGHT_OPTIONS.map((opt) => (
                                                <SelectItem key={opt.value} value={opt.value}>
                                                  {opt.label}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                          {highlightDesc ? (
                                            <p className="mt-2 text-xs text-gray-500 leading-snug">{highlightDesc}</p>
                                          ) : null}
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                          {pfSectionTitle('Inner Diameter')}
                                          {pfField('nominalIdMin', 'Minimum', '0', { defaultZero: true })}
                                          {pfField('nominalIdMax', 'Maximum', '0', { defaultZero: true })}
                                          {pfField('oORIDTolerance', 'Out-of-roundness tolerance', '0', { defaultZero: true })}
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                          {pfSectionTitle('Outer Diameter')}
                                          {pfField('nominalOdMin', 'Minimum', '0', { defaultZero: true })}
                                          {pfField('nominalOdMax', 'Maximum', '0', { defaultZero: true })}
                                          {pfField('oORODTolerance', 'Out-of-roundness tolerance', '0', { defaultZero: true })}
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                          {pfSectionTitle('Seam exclusion')}
                                          <div>
                                            <Label htmlFor={`weldSeamExclusionDeg-${index}`}>Weld Seam Exclusion (deg)</Label>
                                            <NumberInput id={`weldSeamExclusionDeg-${index}`} min={0} step={0.01} value={pipe.weldSeamExclusionDeg ?? ''} onChange={(v) => handlePipeSizeChange(index, 'weldSeamExclusionDeg', v === '' ? undefined : Number(v))} className="mt-2" placeholder="e.g. 30" disabled={!canEdit} />
                                          </div>
                                          {/* {pfField('hiloThreshold', 'HiLo Threshold', 'e.g. 1')} */}
                                          <div>
                                            <Label htmlFor={`hiloThreshold-${index}`}>HiLo Threshold</Label>
                                            <NumberInput
                                              id={`hiloThreshold-${index}`}
                                              min={0}
                                              step={0.01}
                                              value={pipe.hiloThreshold ?? ''}
                                              onChange={(v) => handlePipeSizeChange(index, 'hiloThreshold', v === '' ? undefined : Number(v))}
                                              showUnitSelect
                                              unitValue={pipe.units?.pipefitterFieldUnits?.hiloThreshold || 'mm'}
                                              unitOptions={UNIT_OPTIONS.filter((o) => o.value === 'mm')}
                                              onUnitChange={(v) => handlePipefitterFieldUnitChange(index, 'hiloThreshold', v)}
                                              unitSelectWrapperClassName="mt-2"
                                              placeholder="e.g. 1"
                                              disabled={!canEdit}
                                            />
                                          </div>
                                          <div>
                                            <Label htmlFor={`sixOclockExclusionDeg-${index}`}>6 o'clock Exclusion (deg)</Label>
                                            <NumberInput
                                              id={`sixOclockExclusionDeg-${index}`}
                                              min={0}
                                              step={1}
                                              max={90}
                                              value={pipe.sixOclockExclusionDeg ?? ''}
                                              onChange={(v) => handleSixOclockExclusionChange(index, v)}
                                              className="mt-2"
                                              placeholder="e.g. 60"
                                              disabled={!canEdit}
                                            />
                                          </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                          {pfSectionTitle('Length & targets')}
                                          <div>
                                            <Label htmlFor={`nominalPipeLength-${index}`}>Nominal Pipe Length</Label>
                                            <NumberInput
                                              id={`nominalPipeLength-${index}`}
                                              min={0}
                                              step={0.01}
                                              value={pipe.nominalPipeLength ?? ''}
                                              onChange={(v) => handlePipeSizeChange(index, 'nominalPipeLength', v === '' ? undefined : Number(v))}
                                              showUnitSelect
                                              unitValue="ft"
                                              unitOptions={UNIT_OPTIONS.filter((o) => o.value === 'ft')}
                                              onUnitChange={(v) => handlePipefitterFieldUnitChange(index, 'nominalPipeLength', v)}
                                              unitSelectWrapperClassName="mt-2"
                                              placeholder="e.g. 12.5"
                                              disabled={!canEdit}
                                            />
                                          </div>
                                          <div>
                                            <Label htmlFor={`targetNumberOfPipes-${index}`}>Target Number of Pipes</Label>
                                            <NumberInput id={`targetNumberOfPipes-${index}`} min={0} step={1} value={pipe.targetNumberOfPipes ?? ''} onChange={(v) => handlePipeSizeChange(index, 'targetNumberOfPipes', v === '' ? undefined : Number(v))} className="mt-2" placeholder="e.g. 100" disabled={!canEdit} />
                                          </div>
                                          <div>
                                            <Label htmlFor={`targetPipelineLength-${index}`}>Target Pipeline Length</Label>
                                            <NumberInput
                                              id={`targetPipelineLength-${index}`}
                                              min={0}
                                              step={0.01}
                                              value={pipe.targetPipelineLength ?? ''}
                                              onChange={(v) => handlePipeSizeChange(index, 'targetPipelineLength', v === '' ? undefined : Number(v))}
                                              showUnitSelect
                                              unitValue="ft"
                                              unitOptions={UNIT_OPTIONS.filter((o) => o.value === 'ft')}
                                              onUnitChange={(v) => handlePipefitterFieldUnitChange(index, 'targetPipelineLength', v)}
                                              unitSelectWrapperClassName="mt-2"
                                              placeholder="e.g. 1250"
                                              disabled={!canEdit}
                                            />
                                          </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                          {pfSectionTitle('Dimensions & material')}
                                          <div>
                                            <Label htmlFor={`nominalOd-${index}`}>Nominal OD</Label>
                                            <NumberInput
                                              id={`nominalOd-${index}`}
                                              min={0}
                                              step={0.01}
                                              value={pipe.nominalOd ?? ''}
                                              onChange={(v) =>
                                                handlePipeSizeChange(
                                                  index,
                                                  'nominalOd',
                                                  v === '' ? undefined : Number(v)
                                                )
                                              }
                                              showUnitSelect
                                              unitValue="mm"
                                              unitOptions={UNIT_OPTIONS.filter((o) => o.value === 'mm')}
                                              onUnitChange={(v) =>
                                                handlePipefitterFieldUnitChange(index, 'nominalOd', v)
                                              }
                                              unitSelectWrapperClassName="mt-2"
                                              placeholder="e.g. 508"
                                              disabled={!canEdit}
                                            />
                                          </div>
                                          <div>
                                            <Label htmlFor={`pipeMaterial-${index}`}>Pipe Material</Label>
                                            <Input id={`pipeMaterial-${index}`} value={pipe.pipeMaterial ?? ''} onChange={(e) => handlePipeSizeChange(index, 'pipeMaterial', e.target.value.trim() || undefined)} className="mt-2" placeholder="e.g. Carbon Steel" disabled={!canEdit} />
                                          </div>
                                          <div>
                                            <Label htmlFor={`pipeRotation-${index}`}>Pipe Rotation</Label>
                                            <Select value={pipe.pipeRotation ?? ''} onValueChange={(v) => handlePipeSizeChange(index, 'pipeRotation', v || undefined)} disabled={!canEdit}>
                                              <SelectTrigger id={`pipeRotation-${index}`} className="mt-2"><SelectValue placeholder="Select rotation" /></SelectTrigger>
                                              <SelectContent>{PIPE_ROTATION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                                            </Select>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  <div className="rounded-lg border border-gray-200 overflow-hidden">
                                    <button
                                      type="button"
                                      onClick={() => toggleOptifitExpand(index)}
                                      className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                                      aria-expanded={isOptifitExpanded}
                                    >
                                      {isOptifitExpanded ? (
                                        <ChevronDown className="h-4 w-4 text-heritage-grey flex-shrink-0" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4 text-heritage-grey flex-shrink-0" />
                                      )}
                                      <span className="text-sm font-medium text-gray-600">Optifit</span>
                                    </button>
                                    {isOptifitExpanded && (
                                      <div className="px-4 pb-4 pt-3 space-y-6 border-t border-gray-200">
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                          {pfSectionTitle('Pipe type & sequence')}
                                          <div>
                                            <Label htmlFor={`pipeType-${index}`}>Pipe type</Label>
                                            <Select value={pipe.pipeType && PIPE_TYPE_OPTIONS.some((o) => o.value === pipe.pipeType) ? pipe.pipeType : ''} onValueChange={(v) => handlePipeSizeChange(index, 'pipeType', v || undefined)} disabled={!canEdit}>
                                              <SelectTrigger id={`pipeType-${index}`} className="mt-2"><SelectValue placeholder="Select pipe type" /></SelectTrigger>
                                              <SelectContent>{PIPE_TYPE_OPTIONS.map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}</SelectContent>
                                            </Select>
                                          </div>
                                          <div>
                                            <Label htmlFor={`pipeMode-${index}`}>Pipe mode</Label>
                                            <Select value={pipe.pipeMode && PIPE_MODE_OPTIONS.some((o) => o.value === pipe.pipeMode) ? pipe.pipeMode : ''} onValueChange={(v) => handlePipeSizeChange(index, 'pipeMode', v || undefined)} disabled={!canEdit}>
                                              <SelectTrigger id={`pipeMode-${index}`} className="mt-2"><SelectValue placeholder="Select pipe mode" /></SelectTrigger>
                                              <SelectContent>{PIPE_MODE_OPTIONS.map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}</SelectContent>
                                            </Select>
                                          </div>
                                          <div>
                                            <Label htmlFor={`pipeSequence-${index}`}>Pipe sequence</Label>
                                            <Select value={pipe.pipeSequence != null && PIPE_SEQUENCE_OPTIONS.some((o) => o.value === String(pipe.pipeSequence)) ? String(pipe.pipeSequence) : ''} onValueChange={(v) => handlePipeSizeChange(index, 'pipeSequence', v || undefined)} disabled={!canEdit}>
                                              <SelectTrigger id={`pipeSequence-${index}`} className="mt-2"><SelectValue placeholder="Select sequence" /></SelectTrigger>
                                              <SelectContent>{PIPE_SEQUENCE_OPTIONS.map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}</SelectContent>
                                            </Select>
                                          </div>
                                          {pfField('pipeInnerDiameter', 'Pipe Inner Diameter', 'e.g. 508')}
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                          {pfSectionTitle('Seam & angular limits')}
                                          {pfField('toeSeamSeparationMinMm', 'Toe Seam Separation, min', 'e.g. 200')}
                                          {pfField('seamCapWidthMaxMm', 'Seam Cap Width, max', 'e.g. 22')}
                                          <div>
                                            <Label htmlFor={`sixOclockExclusionDeg-${index}`}>6 o'clock Exclusion (deg)</Label>
                                            <NumberInput
                                              id={`sixOclockExclusionDeg-${index}`}
                                              min={0}
                                              step={1}
                                              max={90}
                                              value={pipe.sixOclockExclusionDeg ?? ''}
                                              onChange={(v) => handleSixOclockExclusionChange(index, v)}
                                              className="mt-2"
                                              placeholder="e.g. 60"
                                              disabled={!canEdit}
                                            />
                                          </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                          {pfSectionTitle('HiLo limits')}
                                          {pfField('hiloWpsLimitMm', 'Hilo WPS Limit', 'e.g. 1')}
                                          <div>
                                            <Label htmlFor={`excessiveHiloLengthLimitDeg-${index}`}>Excessive Hilo Length Limit (deg)</Label>
                                            <NumberInput
                                              id={`excessiveHiloLengthLimitDeg-${index}`}
                                              min={0}
                                              step={0.01}
                                              value={pipe.excessiveHiloLengthLimitDeg ?? ''}
                                              onChange={(v) => handlePipeSizeChange(index, 'excessiveHiloLengthLimitDeg', v === '' ? undefined : Number(v))}
                                              className="mt-2"
                                              placeholder="e.g. 0"
                                              disabled={!canEdit}
                                            />
                                          </div>
                                          {pfField('hiloPreferredLimitTopMm', 'Hilo Preferred Limit, Top', 'e.g. 1')}
                                          {pfField('hiloPreferredLimitMiddleMm', 'Hilo Preferred Limit, Middle', 'e.g. 1')}
                                          {pfField('hiloPreferredLimitLowerMm', 'Hilo Preferred Limit, Lower', 'e.g. 0.5')}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </>
                      )}
                      {/* Spools-specific fields stay below the base pipe details. */}
                      {state.projectSubType === 'spools' && additionalFields && additionalFields(pipe, index, {
                        handleUnitChange,
                        UNIT_OPTIONS,
                        defaultUnit,
                        canEdit,
                        showValidation: pipeShowValidation,
                        validateField: (v: any) => validateField(v, 'numberOfSpools'),
                        isChangingPipeCount,
                      })}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Add New Manufacturer Dialog */}
      <AddNewItemDialog
        open={isAddingManufacturer}
        onOpenChange={setIsAddingManufacturer}
        title="Add New Manufacturer"
        label="Manufacturer Name"
        placeholder="Enter manufacturer name"
        onAdd={handleAddManufacturer}
        buttonText="Add Manufacturer"
        cancelText="Cancel"
      />

      {pipeQR.modal}
      {pipeQR.allPipesEmailModal}

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteModalVisible} onOpenChange={() => {
        handleCancelDelete();
        setIsDeleteModalVisible(false);
      }}>
        <DialogContent className="max-w-[700px] p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 bg-hero-red-10 rounded-full flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-hero-red" />
            </div>
            <DialogTitle className="font-heading text-[18px] font-semibold text-blueprint-blue leading-tight">
              Delete Pipe Details
            </DialogTitle>
          </div>
          {(() => {

            const hasProduction = itemToDelete?.productionData?.isProductionAvailable;
            const hasMaterials = itemToDelete?.materialsData?.isMaterialsAvailable;
            const hasDocuments = itemToDelete?.documentsData?.isDocumentsAvailable;

            if (hasProduction || hasMaterials || hasDocuments) {
              return (
                <div>
                  <div className="mb-3">

                    <p className="text-[13px] leading-5 text-text-grey mb-3">Cannot delete pipe {itemToDelete?.index + 1} because it has associated {hasDocuments ? 'documents' : hasMaterials ? 'materials' : 'production'} data.</p>

                  </div>

                  <div className="mt-4 p-3 bg-hero-red-10 border border-hero-red-20 rounded-md">
                    <p className="text-[13px] leading-5 text-readable-red">
                      <span className="font-semibold">Action Required:</span>
                      <br />
                      <span className="block mt-1">• Please remove all associated {hasDocuments ? 'documents' : hasMaterials ? 'materials' : 'production'} data before deleting this pipe.</span>

                    </p>
                  </div>
                </div>
              );
            } else {
              return (
                <div>
                  <p className="text-[13px] leading-5 text-text-grey mb-3">
                    Are you sure you want to <span className="font-semibold text-readable-red">delete</span> the {title.toLowerCase()} for <span className="font-semibold text-readable-red"> pipe {itemToDelete?.index + 1} </span> ?
                  </p>

                  <div className="bg-page-bg border border-[#E2E5EA] p-3 rounded-md">
                    <p className="text-[12px] text-heritage-grey mb-2">Pipe Details:</p>
                    <div className="max-h-32 overflow-y-auto">
                      <div className="text-[13px] text-text-grey py-2 border-b border-[#E2E5EA] last:border-b-0">
                        <div className="font-medium mb-1">Pipe {itemToDelete?.index + 1}</div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          <div>• Size: {itemToDelete?.pipeName}</div>
                          <div>• Wall Thickness: {itemToDelete?.wallThickness}</div>
                          <div>• Number of Pipes: {itemToDelete?.numberOfPipes}</div>
                          <div>• Length: {itemToDelete?.pipeLength}</div>
                          {state.projectType === 'fabrication' && (
                            <>
                              {state.projectSubType === 'buckleArrestor' && itemToDelete?.numberOfBuckleArrestors && (
                                <div>• Buckle Arrestors: {itemToDelete?.numberOfBuckleArrestors}</div>
                              )}
                              {state.projectSubType === 'bulkhead' && itemToDelete?.numberOfBuckleArrestors && (
                                <div>• Buckle Arrestors: {itemToDelete?.numberOfBuckleArrestors}</div>
                              )}
                              {state.projectSubType === 'buckleArrestor' && itemToDelete?.numberOfForges && (
                                <div>• Forges: {itemToDelete?.numberOfForges}</div>
                              )}
                              {(state.projectSubType === 'buckleArrestor' || state.projectSubType === 'bulkhead') && itemToDelete?.outputAssemblyLength && (
                                <div>• Output Assembly Length: {itemToDelete?.outputAssemblyLength}</div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-hero-red-10 border border-hero-red-20 rounded-md">
                    <p className="text-[13px] leading-5 text-readable-red">
                      <span className="font-semibold">Warning:</span> This action will permanently delete the pipe details shown above.
                    </p>
                  </div>
                </div>
              );
            }
          })()}

          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={handleCancelDelete}
              disabled={deleteLoading}
              className="min-w-[100px]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              // disabled={deleteLoading || itemToDelete?.productionData?.isProductionAvailable || itemToDelete?.materialsData?.isMaterialsAvailable || itemToDelete?.documentsData?.isDocumentsAvailable}
              className={`min-w-[100px] ${itemToDelete?.productionData?.isProductionAvailable || itemToDelete?.materialsData?.isMaterialsAvailable || itemToDelete?.documentsData?.isDocumentsAvailable
                ? 'cursor-not-allowed'
                : 'bg-hero-red hover:bg-hero-red-60 text-white'
                }`}
              loading={deleteLoading}
              loadingText="Deleting..."
            >

              <div className="flex items-center gap-2">
                <Trash2 className="h-4 w-4" />
                {'Delete'}
              </div>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decrease Confirmation Dialog */}
      <Dialog open={isChangeModalVisible} onOpenChange={() => {
        handleCancelChange();
        setIsChangeModalVisible(false);
      }}>
        <DialogContent className="max-w-[700px] p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 bg-hero-red-10 rounded-full flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-hero-red" />
            </div>
            <DialogTitle className="font-heading text-[18px] font-semibold text-blueprint-blue leading-tight">
              Delete Pipe Details
            </DialogTitle>
          </div>

          {(() => {
            const hasProduction = originalPipeSizes.slice(newPipeCount).some((pipe: any) => pipe.productionData?.isProductionAvailable);
            const hasMaterials = originalPipeSizes.slice(newPipeCount).some((pipe: any) => pipe.materialsData?.isMaterialsAvailable);
            const hasDocuments = originalPipeSizes.slice(newPipeCount).some((pipe: any) => pipe.documentsData?.isDocumentsAvailable);

            if (hasProduction || hasMaterials || hasDocuments) {
              return (
                <div>
                  <div className="mb-3">
                    <p className="text-[13px] leading-5 text-text-grey mb-2">Cannot decrease the number of pipe details because some pipes have associated {hasDocuments ? 'documents' : hasMaterials ? 'materials' : 'production'} data.</p>



                  </div>

                  <div className="mt-4 p-3 bg-hero-red-10 border border-hero-red-20 rounded-md">
                    <p className="text-[13px] leading-5 text-readable-red">
                      <span className="font-semibold">Action Required:</span>
                      <br />
                      <span className="block mt-1">• Please remove all associated {hasDocuments ? 'documents' : hasMaterials ? 'materials' : 'production'} data before decreasing the number of pipe sizes.</span>

                    </p>
                  </div>
                </div>
              );
            } else {
              return (
                <div>
                  <p className="text-[13px] leading-5 text-text-grey mb-3">
                    Are you sure you want to <span className="font-semibold text-readable-red">decrease</span> the number of pipe sizes from <span className="font-semibold text-readable-red">{originalPipeCount}</span> to <span className="font-semibold text-readable-red">{newPipeCount}</span>?
                  </p>

                  <div className="bg-page-bg border border-[#E2E5EA] p-3 rounded-md">
                    <p className="text-[12px] text-heritage-grey mb-2">Pipes that will be deleted:</p>
                    <div className="max-h-32 overflow-y-auto">
                      {originalPipeSizes.slice(newPipeCount).map((pipe, index) => (
                        <div key={index} className="text-[13px] text-text-grey py-2 border-b border-[#E2E5EA] last:border-b-0">
                          <div className="font-medium mb-1">Pipe {newPipeCount + index + 1}</div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                            <div>• Size: {pipe.pipeName}</div>
                            <div>• Wall Thickness: {pipe.wallThickness}</div>
                            <div>• Number of Pipes: {pipe.numberOfPipes}</div>
                            <div>• Length: {pipe.pipeLength}</div>
                            {state.projectType === 'fabrication' && (
                              <>
                                {state.projectSubType === 'buckleArrestor' && pipe.numberOfBuckleArrestors && (
                                  <div>• Buckle Arrestors: {pipe.numberOfBuckleArrestors}</div>
                                )}
                                {state.projectSubType === 'bulkhead' && pipe.numberOfBuckleArrestors && (
                                  <div>• Buckle Arrestors: {pipe.numberOfBuckleArrestors}</div>
                                )}
                                {state.projectSubType === 'buckleArrestor' && pipe.numberOfForges && (
                                  <div>• Forges: {pipe.numberOfForges}</div>
                                )}
                                {(state.projectSubType === 'buckleArrestor' || state.projectSubType === 'bulkhead') && pipe.outputAssemblyLength && (
                                  <div>• Output Assembly Length: {pipe.outputAssemblyLength}</div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-hero-red-10 border border-hero-red-20 rounded-md">
                    <p className="text-[13px] leading-5 text-readable-red">
                      <span className="font-semibold">Warning:</span> This action will permanently delete the pipe details shown above.
                    </p>
                  </div>
                </div>
              );
            }
          })()}

          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={handleCancelChange}
              className="min-w-[100px]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmChange}
              disabled={originalPipeSizes.slice(newPipeCount).some((pipe: any) => pipe.documentsData?.isDocumentsAvailable
                || pipe.materialsData?.isMaterialsAvailable || pipe.productionData?.isProductionAvailable
              )}
              className={`min-w-[100px] ${originalPipeSizes.slice(newPipeCount).some((pipe: any) => pipe.documentsData?.isDocumentsAvailable
                || pipe.materialsData?.isMaterialsAvailable || pipe.productionData?.isProductionAvailable
              )
                ? 'cursor-not-allowed'
                : 'bg-hero-red hover:bg-hero-red-60 text-white'
                }`}
              loading={deleteLoading}
              loadingText="Deleting..."
            >

              <div className="flex items-center gap-2">
                <Trash2 className="h-4 w-4" />
                {'Delete'}
              </div>

            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}; 